// ════════════════════════════════════════════════════════════════════
// Glass-i — Stage SALES-RECEIPTS-1 · pure helpers for the Customer Receipts
// page. No React, no network — unit-testable. Sales records DRAFT receipts;
// Accounts (the post permission) posts them to the GL + cancels.
// ════════════════════════════════════════════════════════════════════

export const PERM = Object.freeze({
  VIEW:   'erp.sales.customer.receipt.view',
  CREATE: 'erp.sales.customer.receipt.create',
  POST:   'erp.sales.customer.receipt.post'
});

// Wildcard-aware permission check (mirrors proformaHelpers.hasPermission).
export function hasPermission(permissions, key) {
  if (!Array.isArray(permissions)) return false;
  return permissions.includes('*') || permissions.includes(key);
}

export const RECEIPT_MODES = Object.freeze(['cash', 'bank', 'upi', 'cheque', 'rtgs', 'neft', 'imps', 'adjustment']);
const MODE_LABEL = { cash: 'Cash', bank: 'Bank', upi: 'UPI', cheque: 'Cheque', rtgs: 'RTGS', neft: 'NEFT', imps: 'IMPS', adjustment: 'Adjustment' };
export function modeLabel(m) { return MODE_LABEL[m] || (m == null ? '—' : String(m)); }

const STATUS_LABEL = { draft: 'Draft', posted: 'Posted', cancelled: 'Cancelled' };
export function receiptStatusLabel(s) { return STATUS_LABEL[s] || (s == null ? '—' : String(s)); }
export function receiptStatusColor(s) {
  if (s === 'posted') return 'green';
  if (s === 'draft') return 'gold';
  if (s === 'cancelled') return 'default';
  return 'default';
}

const PURPOSE_LABEL = { OPENING: 'Opening', ADVANCE: 'Advance (on account)', PI_ADVANCE: 'PI Advance', RECEIPT: 'Receipt' };
export function purposeLabel(p) { return PURPOSE_LABEL[p] || (p == null ? '—' : String(p)); }

// Free-receipt purposes the user may pick (PI_ADVANCE is derived from the PI link).
export const FREE_RECEIPT_PURPOSES = Object.freeze(['RECEIPT', 'ADVANCE', 'OPENING']);

// On-account (unapplied) advance from a position — a lump sum not tied to a
// specific proforma. Tolerant of the older `advancesGeneral` field name.
export function onAccountAdvance(position) {
  if (!position) return 0;
  const v = position.onAccountAdvance != null ? position.onAccountAdvance : position.advancesGeneral;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function canRecordReceipt(permissions) { return hasPermission(permissions, PERM.CREATE); }
export function canPostReceipt(permissions) { return hasPermission(permissions, PERM.POST); }

// Row actions: only Accounts (the post permission) may post a draft or cancel a
// posted receipt. Cancelled rows are terminal.
export function receiptActionsFor(row, permissions) {
  const none = { post: false, cancel: false };
  if (!row) return none;
  const canPost = canPostReceipt(permissions);
  return {
    post: canPost && row.status === 'draft',
    cancel: canPost && row.status === 'posted'
  };
}

export function formatMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Status filter tabs → server `status` param.
export const RECEIPT_STATUS_TABS = Object.freeze([
  { key: 'all', label: 'All', status: undefined },
  { key: 'draft', label: 'Drafts', status: 'draft' },
  { key: 'posted', label: 'Posted', status: 'posted' },
  { key: 'cancelled', label: 'Cancelled', status: 'cancelled' }
]);
export function statusForTab(tabKey) {
  const t = RECEIPT_STATUS_TABS.find((x) => x.key === tabKey);
  return t ? t.status : undefined;
}

// A short over-limit banner message for a customer position (or null).
export function overLimitMessage(position) {
  if (!position || !position.overLimit) return null;
  return `Over the credit limit by ${formatMoney(position.overLimitBy)} — consider escalating to Management Approval.`;
}
